import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Download as DownloadIcon,
  CloudDownload as CloudDownloadIcon,
  Storage as StorageIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const DataExport = () => {
  const [loading, setLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [selectedCollections, setSelectedCollections] = useState({
    yearly_graph: true,
    monthly_grpahs: true,
    users: true,
    ledgers: false,
    transection_requests: false,
  });
  const [exportFormat, setExportFormat] = useState("json");
  const [exportHistory, setExportHistory] = useState([]);
  const [collectionStats, setCollectionStats] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    loadCollectionStats();
    loadExportHistory();
  }, []);

  const loadCollectionStats = async () => {
    try {
      const collections = Object.keys(selectedCollections);
      const stats = {};

      for (const collectionName of collections) {
        try {
          const q = query(collection(DATABASE, collectionName), limit(1000));
          const snapshot = await getDocs(q);
          stats[collectionName] = snapshot.size;
        } catch (err) {
          console.error(`Error loading ${collectionName} stats:`, err);
          stats[collectionName] = 0;
        }
      }

      setCollectionStats(stats);
    } catch (error) {
      console.error("Error loading collection stats:", error);
    }
  };

  const loadExportHistory = () => {
    // Load from localStorage or implement proper history tracking
    const history = JSON.parse(localStorage.getItem("exportHistory") || "[]");
    setExportHistory(history);
  };

  const saveExportHistory = (exportData) => {
    const history = JSON.parse(localStorage.getItem("exportHistory") || "[]");
    const newEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      collections: Object.keys(selectedCollections).filter(key => selectedCollections[key]),
      format: exportFormat,
      recordCount: exportData.totalRecords,
      fileName: exportData.fileName,
    };
    
    const updatedHistory = [newEntry, ...history].slice(0, 10); // Keep last 10 exports
    localStorage.setItem("exportHistory", JSON.stringify(updatedHistory));
    setExportHistory(updatedHistory);
  };

  const handleCollectionChange = (collectionName) => {
    setSelectedCollections(prev => ({
      ...prev,
      [collectionName]: !prev[collectionName]
    }));
  };

  const handleExport = async () => {
    const selectedCount = Object.values(selectedCollections).filter(Boolean).length;
    
    if (selectedCount === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Collections Selected",
        text: "Please select at least one collection to export",
      });
      return;
    }

    setLoading(true);
    setExportProgress(0);

    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        collections: {},
        totalRecords: 0,
      };

      const collections = Object.keys(selectedCollections).filter(key => selectedCollections[key]);
      const totalCollections = collections.length;

      for (let i = 0; i < collections.length; i++) {
        const collectionName = collections[i];
        setExportProgress((i / totalCollections) * 100);

        try {
          const q = query(collection(DATABASE, collectionName), orderBy("created_at", "desc"));
          const querySnapshot = await getDocs(q);
          
          const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            created_at: doc.data().created_at?.toDate?.() || new Date(),
            source_collection: collectionName,
          }));

          exportData.collections[collectionName] = data;
          exportData.totalRecords += data.length;
        } catch (error) {
          console.error(`Error exporting ${collectionName}:`, error);
          exportData.collections[collectionName] = [];
        }
      }

      setExportProgress(100);

      // Generate filename
      const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
      const fileName = `fiddux_backup_${timestamp}.${exportFormat}`;

      // Save export history
      saveExportHistory({ ...exportData, fileName });

      // Download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Export Successful",
        text: `Exported ${exportData.totalRecords} records from ${totalCollections} collections`,
      });

    } catch (error) {
      console.error("Error during export:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Failed to export data. Please try again.",
      });
    } finally {
      setLoading(false);
      setExportProgress(0);
    }
  };

  const handleDeleteHistory = (itemId) => {
    setItemToDelete(itemId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteHistory = () => {
    const updatedHistory = exportHistory.filter(item => item.id !== itemToDelete);
    localStorage.setItem("exportHistory", JSON.stringify(updatedHistory));
    setExportHistory(updatedHistory);
    setDeleteDialogOpen(false);
  };

  const getCollectionIcon = (collectionName) => {
    switch (collectionName) {
      case "yearly_graph":
        return <StorageIcon sx={{ color: "#FF6B6B" }} />;
      case "monthly_grpahs":
        return <StorageIcon sx={{ color: "#4ECDC4" }} />;
      case "users":
        return <StorageIcon sx={{ color: "#45B7D1" }} />;
      case "ledgers":
        return <StorageIcon sx={{ color: "#F093FB" }} />;
      case "transection_requests":
        return <StorageIcon sx={{ color: "#4FACFE" }} />;
      default:
        return <StorageIcon sx={{ color: "#gray" }} />;
    }
  };

  const selectedCount = Object.values(selectedCollections).filter(Boolean).length;
  const totalRecords = Object.values(collectionStats).reduce((sum, count) => sum + count, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Data Export & Backup
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Export and backup your database collections for analysis, migration, or disaster recovery.
      </Typography>

      {/* Export Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Configuration
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Select Collections
              </Typography>
              <FormGroup>
                {Object.entries(selectedCollections).map(([collectionName, isSelected]) => (
                  <FormControlLabel
                    key={collectionName}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleCollectionChange(collectionName)}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {getCollectionIcon(collectionName)}
                        <Typography sx={{ ml: 1 }}>
                          {collectionName.replace("_", " ").toUpperCase()}
                        </Typography>
                        <Chip
                          label={collectionStats[collectionName] || 0}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Export Format
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportFormat === "json"}
                      onChange={() => setExportFormat("json")}
                    />
                  }
                  label="JSON Format"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportFormat === "csv"}
                      onChange={() => setExportFormat("csv")}
                      disabled
                    />
                  }
                  label="CSV Format (Coming Soon)"
                />
              </FormGroup>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedCount} collections • Total Records: {totalRecords}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || selectedCount === 0}
              size="large"
            >
              {loading ? "Exporting..." : "Export Data"}
            </Button>
          </Box>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Export Progress: {Math.round(exportProgress)}%
              </Typography>
              <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1 }}>
                <Box
                  sx={{
                    width: `${exportProgress}%`,
                    bgcolor: "primary.main",
                    height: 8,
                    borderRadius: 1,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export History */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
              Export History
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadExportHistory}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {exportHistory.length === 0 ? (
            <Alert severity="info">
              No export history found. Start by exporting your data.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Collections</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>FileName</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportHistory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {dayjs(item.timestamp).format("DD/MM/YYYY HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {item.collections.map((collection) => (
                            <Chip
                              key={collection}
                              label={collection.replace("_", " ")}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.format.toUpperCase()} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {item.recordCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.fileName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete History Entry">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteHistory(item.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Export History</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this export history entry?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteHistory} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataExport;
